import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  analytics,
  deliverability,
  subscribers,
  type DashboardAnalytics,
  type DeliverabilityResponse,
} from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  ShieldCheck, ShieldAlert, TrendingUp, TrendingDown,
  AlertTriangle, Mail, Users, Zap, BarChart3,
  CheckCircle, XCircle, Clock, Flame, ArrowRight,
  RefreshCw, Heart, Activity, Target, ChevronRight,
} from "lucide-react";

// ── Health score calculation ──
interface HealthMetric {
  name: string;
  score: number;        // 0-100
  weight: number;       // how much it contributes to overall
  status: "good" | "warning" | "critical";
  label: string;
  detail: string;
  icon: any;
}

function calcHealthMetrics(
  dash: DashboardAnalytics | null,
  deliv: DeliverabilityResponse | null,
  engagement: { tier: string; count: number; avgScore: number }[] | null,
): HealthMetric[] {
  const metrics: HealthMetric[] = [];

  if (!dash || !deliv) return metrics;

  const sent = deliv.totals.sent;

  // 1. Open Rate
  const openRate = deliv.totals.openRate;
  const openScore = sent === 0 ? 50 : Math.min(100, Math.round(openRate * 4.5));
  metrics.push({
    name: "Open Rate",
    score: openScore,
    weight: 25,
    status: openRate >= 20 ? "good" : openRate >= 12 ? "warning" : "critical",
    label: sent > 0 ? `${openRate}%` : "No data",
    detail: sent === 0 ? "Send your first campaign to see your open rate."
      : openRate >= 20 ? "Excellent! Your open rate is above industry average."
      : openRate >= 12 ? "Room to improve. Try A/B testing subject lines."
      : "Below average. Review your subject lines and sending frequency.",
    icon: Mail,
  });

  // 2. Bounce Rate
  const bounceRate = deliv.totals.bounceRate;
  const bounceScore = sent === 0 ? 80 : bounceRate <= 1 ? 100 : bounceRate <= 3 ? 70 : bounceRate <= 5 ? 40 : 10;
  metrics.push({
    name: "Bounce Rate",
    score: bounceScore,
    weight: 25,
    status: bounceRate <= 2 ? "good" : bounceRate <= 5 ? "warning" : "critical",
    label: sent > 0 ? `${bounceRate}%` : "No data",
    detail: sent === 0 ? "No bounces recorded yet."
      : bounceRate <= 2 ? "Healthy bounce rate. Your list is clean."
      : bounceRate <= 5 ? "Slightly elevated. Consider cleaning invalid addresses."
      : "High bounce rate! Clean your list immediately to protect sender reputation.",
    icon: ShieldCheck,
  });

  // 3. Sender Reputation
  const repScore = deliv.totals.reputationScore;
  const repDisplay = repScore !== null ? repScore : null;
  metrics.push({
    name: "Sender Reputation",
    score: repScore ?? 50,
    weight: 25,
    status: repScore === null ? "warning" : repScore >= 80 ? "good" : repScore >= 50 ? "warning" : "critical",
    label: repDisplay !== null ? `${repDisplay}/100` : "Pending",
    detail: repScore === null ? "Send emails to build your reputation score."
      : repScore >= 80 ? "Strong reputation. Keep it up!"
      : repScore >= 50 ? "Moderate reputation. Reduce bounces and improve engagement."
      : "Low reputation. Your emails may be hitting spam folders.",
    icon: Activity,
  });

  // 4. List Health (engagement tiers)
  if (engagement && engagement.length > 0) {
    const totalSubs = engagement.reduce((sum, t) => sum + t.count, 0);
    const hotWarm = engagement.filter(t => t.tier === "hot" || t.tier === "warm").reduce((sum, t) => sum + t.count, 0);
    const cold = engagement.find(t => t.tier === "cold")?.count || 0;
    const engagedPct = totalSubs > 0 ? Math.round((hotWarm / totalSubs) * 100) : 0;
    const coldPct = totalSubs > 0 ? Math.round((cold / totalSubs) * 100) : 0;

    const listScore = totalSubs === 0 ? 50 : Math.min(100, Math.round(engagedPct * 1.2));
    metrics.push({
      name: "List Engagement",
      score: listScore,
      weight: 25,
      status: engagedPct >= 50 ? "good" : engagedPct >= 25 ? "warning" : "critical",
      label: totalSubs > 0 ? `${engagedPct}% engaged` : "No subscribers",
      detail: totalSubs === 0 ? "Add subscribers to see engagement data."
        : engagedPct >= 50 ? `${hotWarm.toLocaleString()} of ${totalSubs.toLocaleString()} subscribers are actively engaged.`
        : coldPct >= 40 ? `${coldPct}% of your list is cold. Consider running a re-engagement campaign.`
        : "Engagement is moderate. Use targeted campaigns to warm up your list.",
      icon: Users,
    });
  } else {
    metrics.push({
      name: "List Engagement",
      score: 50,
      weight: 25,
      status: "warning",
      label: "No data",
      detail: "Add subscribers and send campaigns to see engagement data.",
      icon: Users,
    });
  }

  return metrics;
}

function overallScore(metrics: HealthMetric[]): number {
  if (metrics.length === 0) return 0;
  const totalWeight = metrics.reduce((sum, m) => sum + m.weight, 0);
  const weightedSum = metrics.reduce((sum, m) => sum + m.score * m.weight, 0);
  return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;
}

function gradeFromScore(score: number): { grade: string; color: string; bg: string } {
  if (score >= 90) return { grade: "A", color: "text-emerald-600", bg: "from-emerald-500 to-emerald-600" };
  if (score >= 75) return { grade: "B", color: "text-emerald-500", bg: "from-emerald-400 to-emerald-500" };
  if (score >= 60) return { grade: "C", color: "text-amber-500", bg: "from-amber-400 to-amber-500" };
  if (score >= 40) return { grade: "D", color: "text-orange-500", bg: "from-orange-400 to-orange-500" };
  return { grade: "F", color: "text-red-500", bg: "from-red-500 to-red-600" };
}

const STATUS_COLORS = {
  good: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-400" },
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-600", dot: "bg-red-500" },
};

export default function EmailHealthPage() {
  const [dash, setDash] = useState<DashboardAnalytics | null>(null);
  const [deliv, setDeliv] = useState<DeliverabilityResponse | null>(null);
  const [engagement, setEngagement] = useState<{ tier: string; count: number; avgScore: number }[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  async function loadAll() {
    try {
      const [dashRes, delivRes, engRes] = await Promise.all([
        analytics.dashboard(),
        deliverability.stats(),
        subscribers.engagementStats(),
      ]);
      setDash(dashRes);
      setDeliv(delivRes);
      setEngagement(engRes.tiers || []);
    } catch (e) {
      console.error("Health dashboard load error:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { loadAll(); }, []);

  function handleRefresh() {
    setRefreshing(true);
    loadAll();
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-20 flex justify-center">
        <Spinner />
      </div>
    );
  }

  const metrics = calcHealthMetrics(dash, deliv, engagement);
  const score = overallScore(metrics);
  const grade = gradeFromScore(score);

  // Derive quick stats
  const totalSubs = parseInt(dash?.subscribers?.total_subscribers || "0");
  const activeSubs = parseInt(dash?.subscribers?.active_subscribers || "0");
  const totalSent = dash?.performance?.total_sent || 0;
  const blacklistClean = deliv?.blacklist ? deliv.blacklist.listedOn.length === 0 : null;
  const warmupActive = deliv?.warmup?.is_active === 1;

  // Actionable recommendations
  const recommendations: { text: string; link: string; linkLabel: string; priority: "high" | "medium" | "low" }[] = [];

  if (metrics.find(m => m.name === "Bounce Rate" && m.status === "critical")) {
    recommendations.push({ text: "Your bounce rate is dangerously high. Clean invalid addresses immediately.", link: "/subscribers", linkLabel: "Clean List", priority: "high" });
  }
  if (metrics.find(m => m.name === "Open Rate" && m.status === "critical")) {
    recommendations.push({ text: "Open rates are below average. Try A/B testing subject lines with AI.", link: "/campaigns/wizard", linkLabel: "AI Wizard", priority: "high" });
  }
  if (metrics.find(m => m.name === "List Engagement" && m.status === "critical")) {
    recommendations.push({ text: "Many subscribers are disengaged. Run a re-engagement automation.", link: "/automations/prebuilt", linkLabel: "Browse Templates", priority: "high" });
  }
  if (blacklistClean === null) {
    recommendations.push({ text: "You haven't run a blacklist check yet. Check your domain reputation.", link: "/deliverability", linkLabel: "Run Check", priority: "medium" });
  }
  if (!warmupActive && totalSent < 500) {
    recommendations.push({ text: "Consider warming up your domain for better inbox placement.", link: "/deliverability", linkLabel: "Start Warmup", priority: "medium" });
  }
  if (totalSent === 0) {
    recommendations.push({ text: "Send your first campaign to start building your sender reputation.", link: "/campaigns", linkLabel: "Create Campaign", priority: "medium" });
  }
  if (metrics.find(m => m.name === "Open Rate" && m.status === "warning")) {
    recommendations.push({ text: "Open rates could be better. Try send-time optimization.", link: "/settings", linkLabel: "Settings", priority: "low" });
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2">
            <Heart size={22} className="text-coral" /> Email Health
          </h1>
          <p className="text-slate text-sm mt-0.5">Your email program's vital signs — all in one place.</p>
        </div>
        <button onClick={handleRefresh} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-sand text-sm font-medium text-slate hover:bg-paper transition-colors disabled:opacity-50">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Overall Score */}
      <div className="bg-white rounded-xl border border-sand p-6 mb-6 flex flex-col sm:flex-row items-center gap-6">
        {/* Score circle */}
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="8" />
            <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8"
              className={`${grade.color.replace("text-", "stroke-")}`}
              strokeDasharray={`${score * 2.64} 264`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`font-display font-bold text-3xl ${grade.color}`}>{score}</span>
            <span className="text-[10px] text-slate">/ 100</span>
          </div>
        </div>

        <div className="text-center sm:text-left flex-1">
          <div className="flex items-center gap-2 justify-center sm:justify-start">
            <span className={`font-display font-bold text-xl ${grade.color}`}>Grade: {grade.grade}</span>
            {score >= 75 && <CheckCircle size={18} className="text-emerald-500" />}
            {score >= 40 && score < 75 && <AlertTriangle size={18} className="text-amber-500" />}
            {score < 40 && <XCircle size={18} className="text-red-500" />}
          </div>
          <p className="text-sm text-slate mt-1">
            {score >= 90 ? "Your email program is in excellent shape. Keep doing what you're doing!" :
             score >= 75 ? "Good health overall. A few tweaks could push you to the top." :
             score >= 60 ? "Moderate health. Address the warnings below to improve." :
             score >= 40 ? "Needs attention. Several metrics require improvement." :
             "Critical issues detected. Take immediate action to protect deliverability."}
          </p>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-3 flex-shrink-0">
          <div className="bg-paper rounded-lg px-3 py-2 text-center">
            <p className="font-display font-bold text-ink text-lg">{activeSubs.toLocaleString()}</p>
            <p className="text-[10px] text-slate">Active Subs</p>
          </div>
          <div className="bg-paper rounded-lg px-3 py-2 text-center">
            <p className="font-display font-bold text-ink text-lg">{totalSent.toLocaleString()}</p>
            <p className="text-[10px] text-slate">Emails Sent</p>
          </div>
          <div className="bg-paper rounded-lg px-3 py-2 text-center">
            {blacklistClean === null ? (
              <p className="font-display font-bold text-slate text-lg">—</p>
            ) : blacklistClean ? (
              <p className="font-display font-bold text-emerald-600 text-lg">Clean</p>
            ) : (
              <p className="font-display font-bold text-red-500 text-lg">Listed</p>
            )}
            <p className="text-[10px] text-slate">Blacklist</p>
          </div>
          <div className="bg-paper rounded-lg px-3 py-2 text-center">
            {warmupActive ? (
              <p className="font-display font-bold text-coral text-lg">Active</p>
            ) : (
              <p className="font-display font-bold text-slate text-lg">Off</p>
            )}
            <p className="text-[10px] text-slate">Warmup</p>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {metrics.map((m) => {
          const colors = STATUS_COLORS[m.status];
          const Icon = m.icon;
          return (
            <div key={m.name} className={`rounded-xl border p-5 ${colors.bg} ${colors.border}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={16} className={colors.text} />
                  <span className="text-sm font-bold text-ink">{m.name}</span>
                </div>
                <span className={`text-lg font-display font-bold ${colors.text}`}>{m.label}</span>
              </div>
              {/* Score bar */}
              <div className="h-2 bg-white/60 rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${colors.dot}`}
                  style={{ width: `${m.score}%` }}
                />
              </div>
              <p className="text-xs text-slate leading-relaxed">{m.detail}</p>
            </div>
          );
        })}
      </div>

      {/* Engagement Breakdown */}
      {engagement && engagement.length > 0 && (
        <div className="bg-white rounded-xl border border-sand p-5 mb-6">
          <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-4">
            <Zap size={15} className="text-coral" /> Engagement Breakdown
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {["hot", "warm", "cold", "new"].map(tier => {
              const data = engagement.find(t => t.tier === tier);
              const count = data?.count || 0;
              const totalCount = engagement.reduce((sum, t) => sum + t.count, 0);
              const pct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
              const tierColors: Record<string, { bg: string; text: string; emoji: string }> = {
                hot:  { bg: "bg-red-50",    text: "text-red-600",    emoji: "🔥" },
                warm: { bg: "bg-amber-50",  text: "text-amber-600",  emoji: "☀️" },
                cold: { bg: "bg-blue-50",   text: "text-blue-600",   emoji: "❄️" },
                new:  { bg: "bg-purple-50", text: "text-purple-600", emoji: "✨" },
              };
              const tc = tierColors[tier] || tierColors.new;
              return (
                <div key={tier} className={`${tc.bg} rounded-lg p-3 text-center`}>
                  <span className="text-lg">{tc.emoji}</span>
                  <p className={`font-display font-bold text-lg ${tc.text}`}>{count.toLocaleString()}</p>
                  <p className="text-[10px] text-slate capitalize">{tier} ({pct}%)</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white rounded-xl border border-sand p-5 mb-6">
          <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-4">
            <Target size={15} className="text-coral" /> Recommendations
          </h3>
          <div className="space-y-3">
            {recommendations.map((rec, i) => {
              const prioColors = {
                high: "border-red-200 bg-red-50",
                medium: "border-amber-200 bg-amber-50",
                low: "border-sand bg-paper",
              };
              return (
                <div key={i} className={`flex items-center justify-between rounded-lg border p-3 ${prioColors[rec.priority]}`}>
                  <div className="flex items-center gap-3 flex-1">
                    {rec.priority === "high" && <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />}
                    {rec.priority === "medium" && <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />}
                    {rec.priority === "low" && <CheckCircle size={14} className="text-slate flex-shrink-0" />}
                    <span className="text-xs text-ink">{rec.text}</span>
                  </div>
                  <Link to={rec.link}
                    className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-coral hover:underline ml-3">
                    {rec.linkLabel} <ChevronRight size={12} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Deliverability", desc: "Warmup & blacklists", to: "/deliverability", icon: ShieldCheck },
          { label: "Campaigns", desc: "Send & manage", to: "/campaigns", icon: Mail },
          { label: "Subscribers", desc: "List management", to: "/subscribers", icon: Users },
          { label: "Automations", desc: "Set up workflows", to: "/automations", icon: Zap },
        ].map((link) => (
          <Link key={link.to} to={link.to}
            className="bg-white rounded-xl border border-sand p-4 hover:border-coral/30 hover:shadow-sm transition-all group">
            <link.icon size={18} className="text-coral mb-2" />
            <p className="text-xs font-bold text-ink group-hover:text-coral transition-colors">{link.label}</p>
            <p className="text-[10px] text-slate">{link.desc}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
